import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody, Field } from "@/components/PageChrome";
import { Input, Select, Button } from "@/components/controls";
import { Plus, Trash2, X, Lock, Upload, Download, Filter, Check, QrCode, Image as ImageIcon, ClipboardCopy, AlertTriangle, Pencil } from "lucide-react";
import { T } from "@/lib/testIds";
import { usePerms, useAuth } from "@/lib/auth";
import PaginationBar from "@/components/PaginationBar";
import useClientPagination from "@/hooks/useClientPagination";
import FieldFilterBar, { applyFieldFilters } from "@/components/FieldFilterBar";
import FilterPresets from "@/components/FilterPresets";
import MultiSelectFilter from "@/components/MultiSelectFilter";

const BANK_FILTER_FIELDS = [
  { key: "payment_transfer_date", label: "Transfer Date", type: "date" },
  { key: "account_id", label: "Account", type: "select", opts: (d) => d.account_id || [] },
  { key: "payment_mode", label: "Payment Mode", type: "select", opts: (d) => d.payment_mode || [] },
  { key: "money_added", label: "Amount ₹", type: "number" },
  { key: "status", label: "Status", type: "select", opts: () => ["pending", "verified", "rejected"] },
  { key: "notes", label: "Notes", type: "text" },
];

// Payment intake card — bank + UPI + QR image mapped to an account_id.
function PaymentDetailsCard({ accountId, isAdmin, onCopyDone }) {
  const [details, setDetails] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [qrPreview, setQrPreview] = useState(null); // local blob URL after upload
  const fileRef = useRef(null);
  const tok = typeof window !== "undefined" ? localStorage.getItem("access_token") || "" : "";
  const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const load = async () => {
    if (!accountId) { setDetails(null); return; }
    try {
      const { data } = await api.get(`/bank/details/${encodeURIComponent(accountId)}`);
      setDetails(data || {});
      setDraft({ ...(data || {}) });
      setQrPreview(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/bank/details/${encodeURIComponent(accountId)}`, {
        bank_name: draft.bank_name || "",
        account_holder_name: draft.account_holder_name || "",
        account_number: draft.account_number || "",
        ifsc: draft.ifsc || "",
        upi_id: draft.upi_id || "",
        notes: draft.notes || "",
      });
      setDetails(data);
      setEditing(false);
      toast.success("Payment details saved");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const uploadQR = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/bank/details/${encodeURIComponent(accountId)}/qr`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setDetails((d) => ({ ...(d || {}), qr_url: data.qr_url }));
      // force <img> refresh with cache-busting
      setQrPreview(`${API}${data.qr_url}?auth=${encodeURIComponent(tok)}&t=${Date.now()}`);
      toast.success("QR uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const copy = async (label, value) => {
    if (!value) return;
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copied`); onCopyDone?.(); }
    catch { toast.error("Copy failed"); }
  };

  if (!accountId) {
    return (
      <div className="border border-dashed border-slate-300 rounded-sm p-3 bg-slate-50 text-[12px] text-slate-600" data-testid="bank-details-empty">
        Pick a specific account above to view its payment details / QR.
      </div>
    );
  }

  const qrSrc = qrPreview || (details?.qr_url ? `${API}${details.qr_url}?auth=${encodeURIComponent(tok)}` : null);

  return (
    <div className="bg-white border border-slate-200 rounded-sm mb-3" data-testid="bank-details-card">
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div>
          <div className="font-heading text-[13px] font-semibold flex items-center gap-2">
            <QrCode size={14} className="text-slate-500" /> Payment Details — {accountId}
          </div>
          <div className="text-[10.5px] text-slate-500">Resellers use these to recharge their balance. Scan the QR or copy any field.</div>
        </div>
        {isAdmin && (
          editing ? (
            <div className="inline-flex gap-2">
              <Button onClick={() => { setEditing(false); setDraft({ ...(details || {}) }); }} testId="bank-details-cancel">Cancel</Button>
              <Button variant="primary" onClick={save} disabled={busy} testId="bank-details-save">Save</Button>
            </div>
          ) : (
            <Button onClick={() => setEditing(true)} testId="bank-details-edit">Edit</Button>
          )
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
        <div className="md:col-span-2 space-y-2">
          {[
            ["Bank Name", "bank_name"],
            ["Account Holder", "account_holder_name"],
            ["Account Number", "account_number"],
            ["IFSC", "ifsc"],
            ["UPI ID", "upi_id"],
            ["Notes", "notes"],
          ].map(([label, key]) => (
            <div key={key} className="flex items-start gap-2">
              <div className="w-28 text-[11.5px] text-slate-500 pt-1">{label}</div>
              {editing ? (
                <Input testId={`bank-details-${key}`} value={draft[key] || ""}
                       onChange={(e) => setDraft((s) => ({ ...s, [key]: e.target.value }))}
                       placeholder={label} />
              ) : (
                <div className="flex-1 flex items-center gap-2 min-h-[26px]">
                  <div className="font-mono text-[13px] text-slate-800 break-all" data-testid={`bank-details-${key}-value`}>
                    {details?.[key] || "—"}
                  </div>
                  {details?.[key] && (
                    <button onClick={() => copy(label, details[key])} className="text-slate-400 hover:text-slate-800" title={`Copy ${label}`} data-testid={`bank-details-${key}-copy`}>
                      <ClipboardCopy size={12} />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center gap-2 border-l border-slate-200 md:pl-4">
          <div className="text-[10.5px] font-semibold text-slate-500">QR / Barcode</div>
          {qrSrc ? (
            <a href={qrSrc} target="_blank" rel="noreferrer" data-testid="bank-details-qr-link">
              <img src={qrSrc} alt="Payment QR" className="w-40 h-40 object-contain border border-slate-200 rounded-sm bg-white" data-testid="bank-details-qr-img" />
            </a>
          ) : (
            <div className="w-40 h-40 flex items-center justify-center border border-dashed border-slate-300 rounded-sm text-slate-400 text-[11px]">
              <ImageIcon size={30} strokeWidth={1} />
            </div>
          )}
          {isAdmin && (
            <>
              <input ref={fileRef} type="file" accept="image/*" className="hidden"
                     onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadQR(f); e.target.value = ""; }}
                     data-testid="bank-details-qr-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={busy} testId="bank-details-qr-upload">
                <Upload size={12} /> {qrSrc ? "Replace QR" : "Upload QR"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Bank() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const myAccountIds = user?.account_ids || [];
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [accountId, setAccountId] = useState("");
  // Feb 27 2026 — multi-select picker for users allocated to more than one
  // account. `pickedIds` holds the selected account_ids (empty = "all"),
  // and `accountId` (kept in sync for backward compat with PaymentDetailsCard)
  // becomes the comma-joined string sent to the API.
  const [pickedIds, setPickedIds] = useState([]);
  const [summary, setSummary] = useState(null);
  const [creating, setCreating] = useState(null);
  const [editingId, setEditingId] = useState(null); // id of the row being edited (null = creating new)
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [advFilters, setAdvFilters] = useState({});
  const [pendingScreenshot, setPendingScreenshot] = useState(null); // File (before save)
  const { canView, canEdit, canDelete } = usePerms();
  const editable = canEdit("bank");
  const deletable = canDelete("bank");
  const tok = typeof window !== "undefined" ? localStorage.getItem("access_token") || "" : "";
  const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const COLS = [
    { k: "payment_transfer_date", label: "Date", field: "payment_transfer_date", render: (r) => <td className="font-mono">{r.payment_transfer_date}</td> },
    { k: "account_id", label: "Account", field: "account_id", render: (r) => <td className="font-mono">{r.account_id}</td> },
    { k: "payment_mode", label: "Mode", field: "payment_mode", render: (r) => <td>{r.payment_mode}</td> },
    { k: "money_added", label: "Amount ₹", field: "money_added", render: (r) => <td className="font-mono">₹{Number(r.money_added).toLocaleString()}</td> },
    { k: "status", label: "Status", field: "status", render: (r) => {
        const s = r.status || "verified";
        const cls = s === "verified"
          ? "bg-emerald-50 border-emerald-300 text-emerald-800"
          : s === "rejected"
            ? "bg-red-50 border-red-300 text-red-800"
            : "bg-amber-50 border-amber-300 text-amber-800";
        return (
          <td>
            <span data-testid={`bank-status-${r.id}`} className={`inline-flex items-center gap-1 h-5 px-1.5 text-[10.5px] font-semibold rounded-sm border ${cls} uppercase`}>
              {s}
            </span>
          </td>
        );
    } },
    { k: "payment_screenshot_url", label: "Screenshot", field: "payment_screenshot_url", render: (r) => (
      <td data-testid={`bank-screenshot-${r.id}`}>
        {r.payment_screenshot_url ? (
          <a href={`${API}${r.payment_screenshot_url}?auth=${encodeURIComponent(tok)}`} target="_blank" rel="noreferrer">
            <img src={`${API}${r.payment_screenshot_url}?auth=${encodeURIComponent(tok)}`} alt="proof"
                 className="h-9 w-9 object-cover border border-slate-200 rounded-sm bg-slate-100 inline-block" />
          </a>
        ) : <span className="text-slate-400">—</span>}
      </td>
    ) },
    { k: "notes", label: "Notes", field: "notes", render: (r) => <td>{r.notes || "—"}</td> },
  ];
  const visibleCols = COLS.filter((c) => canView("bank", c.field));
  // Merge accounts into the dropdown map for the Account filter select
  const filterDropdowns = useMemo(() => ({
    ...dropdowns,
    account_id: [...new Set(accounts.map((a) => a.account_id).filter(Boolean))].sort(),
  }), [dropdowns, accounts]);
  const filteredRows = useMemo(
    () => rows.filter((r) => applyFieldFilters(r, advFilters, BANK_FILTER_FIELDS)),
    [rows, advFilters]
  );
  const activeAdvCount = Object.keys(advFilters).filter((k) => {
    const v = advFilters[k];
    if (!v) return false;
    if (typeof v === "object") return Object.values(v).some((x) => x !== "" && x !== undefined);
    return true;
  }).length;
  const pag = useClientPagination(filteredRows, "bank");

  // Client-side: hide accounts the user is not allocated to (admin sees all).
  const allowedAccounts = accounts.filter((a) => isAdmin || myAccountIds.includes(a.account_id));

  const load = async () => {
    try {
      const joined = pickedIds.length ? pickedIds.join(",") : "";
      // Keep the single-account state in sync for PaymentDetailsCard, which
      // needs exactly one account to render the details card. Multi-select
      // → clear the details card (nothing meaningful to show for many).
      setAccountId(pickedIds.length === 1 ? pickedIds[0] : "");
      const params = joined ? { account_id: joined } : {};
      const [b, a, d, s] = await Promise.all([
        api.get("/bank", { params }),
        api.get("/accounts"),
        api.get("/settings/dropdowns"),
        api.get("/bank/summary", { params }),
      ]);
      setRows(b.data);
      setAccounts(a.data);
      setDropdowns(d.data);
      setSummary(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pickedIds.join(",")]);

  const save = async () => {
    try {
      if (editingId) {
        // PATCH existing row.
        await api.patch(`/bank/${editingId}`, {
          payment_transfer_date: creating.payment_transfer_date,
          payment_mode: creating.payment_mode,
          account_id: creating.account_id,
          money_added: creating.money_added,
          notes: creating.notes,
        });
        // Optionally replace the screenshot on the same row.
        if (pendingScreenshot) {
          const form = new FormData();
          form.append("file", pendingScreenshot);
          try {
            await api.post(`/bank/${editingId}/screenshot`, form,
              { headers: { "Content-Type": "multipart/form-data" } });
          } catch (e) { toast.error(`Row updated but screenshot upload failed: ${formatApiError(e)}`); }
        }
        toast.success(isAdmin ? "Updated" : "Updated — sent back for admin verification");
      } else {
        const { data } = await api.post("/bank", creating);
        if (pendingScreenshot && data?.id) {
          const form = new FormData();
          form.append("file", pendingScreenshot);
          try {
            await api.post(`/bank/${data.id}/screenshot`, form,
              { headers: { "Content-Type": "multipart/form-data" } });
          } catch (e) { toast.error(`Row saved but screenshot upload failed: ${formatApiError(e)}`); }
        }
        toast.success(isAdmin ? "Added & verified" : "Submitted for admin verification");
      }
      setCreating(null);
      setEditingId(null);
      setPendingScreenshot(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const wipeAll = async () => {
    const phrase = "WIPE ALL BANK";
    const entry = window.prompt(
      `This will permanently delete EVERY bank transfer row.\nType "${phrase}" to confirm.`,
    );
    if (entry !== phrase) {
      if (entry !== null) toast.error("Confirmation phrase did not match — wipe cancelled.");
      return;
    }
    try {
      const { data } = await api.post("/bank/admin/wipe", { confirm: phrase });
      toast.success(`Wiped ${data.deleted} bank rows.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const verify = async (rid) => {
    try {
      await api.patch(`/bank/${rid}/verify`);
      toast.success("Verified — counted in balance");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reject = async (rid) => {
    const reason = window.prompt("Reason for rejection (optional):", "") ?? "";
    try {
      await api.patch(`/bank/${rid}/reject`, { reason });
      toast.success("Rejected — kept for audit");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete transfer?")) return;
    try { await api.delete(`/bank/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = async () => {
    try { await downloadFile("/bank/export/csv", "bank.csv"); toast.success("Downloaded bank.csv"); }
    catch (e) { toast.error(String(e.message || e)); }
  };

  const downloadTemplate = async () => {
    try { await downloadFile("/bank/templates/csv", "bank_template.csv"); }
    catch (e) { toast.error(String(e.message || e)); }
  };

  const importCsv = async (file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/bank/import/csv", form, { headers: { "Content-Type": "multipart/form-data" } });
      const skipCount = (data.skipped || []).length;
      toast.success(`Imported ${data.created} transfer${data.created === 1 ? "" : "s"}${skipCount ? ` · ${skipCount} skipped` : ""}`);
      if (skipCount) {
        console.warn("[bank import] skipped rows:", data.skipped);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalTransferPrice = summary?.total_transfer_price ?? 0;
  const totalTransferred = summary?.total_transferred ?? 0;
  const totalPending = summary?.total_pending_transfer ?? 0;
  const totalBuyBack = summary?.total_buy_back ?? 0;
  const balance = summary?.balance ?? 0;

  return (
    <>
      <PageHeader
        title="Bank"
        subtitle={
          isAdmin
            ? "Payment transfers & balance"
            : myAccountIds.length
              ? `Payment transfers • You have access to ${myAccountIds.length} account${myAccountIds.length === 1 ? "" : "s"}`
              : "You have not been allocated to any account. Ask an admin to add you."
        }
        testId={T.moduleTitle("bank")}
        module="bank"
      >
        {!isAdmin && (
          <div className="inline-flex items-center gap-1 h-8 px-2 border border-amber-300 bg-amber-50 text-amber-900 text-[11px] font-semibold rounded-sm" title="You see only banks for your allocated accounts">
            <Lock size={11} /> Account-scoped
          </div>
        )}
        <div data-testid="bank-account-multi-picker">
          {/* Sep 7 2026 — match Dashboard's Account filter UX: search box,
              Select all with X/Y count, per-account checkboxes, Clear + Done. */}
          <MultiSelectFilter
            label="Accounts"
            options={allowedAccounts.map((a) => a.account_id).filter(Boolean)}
            value={pickedIds}
            onChange={setPickedIds}
            testId="bank-account-multi-picker"
          />
        </div>
        <Button onClick={() => setShowFilterBar((s) => !s)} testId="filter-bank-btn">
          <Filter size={13} /> Filters
          {activeAdvCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9.5px] font-bold text-white bg-blue-600 rounded-full">
              {activeAdvCount}
            </span>
          )}
        </Button>
        <FilterPresets
          moduleKey="bank"
          value={advFilters}
          onLoad={setAdvFilters}
        />
        {editable && allowedAccounts.length > 0 && (
          <>
            <Button onClick={downloadTemplate} testId="bank-template-btn">
              <Download size={13} /> Template
            </Button>
            <label className="h-8 px-3 text-[12px] font-medium rounded-sm inline-flex items-center gap-1.5 border border-slate-200 cursor-pointer bg-white hover:bg-slate-50" data-testid="bank-import-btn">
              <Upload size={13} /> Import
              <input type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  importCsv(f).finally(() => { e.target.value = ""; });
                }} />
            </label>
            <Button onClick={exportCsv} testId="bank-export-btn">
              <Download size={13} /> Export CSV
            </Button>
            {isAdmin && (
              <Button onClick={wipeAll} testId="bank-wipe-all-btn" title="Admin only — delete every bank row">
                <AlertTriangle size={13} /> Wipe All
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => setCreating({
                payment_transfer_date: new Date().toISOString().slice(0, 10),
                payment_mode: "Bank Transfer",
                account_id: accountId || allowedAccounts[0]?.account_id || "",
              })}
              testId={T.newBtn("bank")}
            >
              <Plus size={13} /> New Transfer
            </Button>
          </>
        )}
      </PageHeader>
      <PageBody>
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="bank-total-transferred">
              <div className="field-label">Total Transferred (Bank in)</div>
              <div className="font-mono text-2xl font-semibold text-emerald-800">₹{totalTransferred.toLocaleString()}</div>
              <div className="text-[10.5px] text-slate-500 font-mono mt-1">{rows.length} transfer{rows.length === 1 ? "" : "s"}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="bank-total-transfer-price">
              <div className="field-label">Total Transfer Price</div>
              <div className="font-mono text-2xl font-semibold text-indigo-800">₹{totalTransferPrice.toLocaleString()}</div>
              <div className="text-[10.5px] text-slate-500 font-mono mt-1">Orders + Procurement</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="bank-total-buyback">
              <div className="field-label">Total Buy-Back</div>
              <div className="font-mono text-2xl font-semibold text-slate-700">₹{totalBuyBack.toLocaleString()}</div>
              <div className="text-[10.5px] text-slate-500 font-mono mt-1">Σ (buy_back_price × qty)</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="bank-balance">
              <div className="field-label">Balance</div>
              <div className={`font-mono text-2xl font-semibold ${balance < 0 ? "text-red-700" : "text-emerald-700"}`}>
                ₹{balance.toLocaleString()}
              </div>
              <div className="text-[10.5px] text-slate-500 font-mono mt-1">Verified + Buy-back − Transfer Price (orders + procurement){totalPending > 0 ? ` · ₹${totalPending.toLocaleString()} pending verification` : ""}</div>
            </div>
          </div>
        )}

        <PaymentDetailsCard accountId={accountId} isAdmin={isAdmin} />

        {!isAdmin && myAccountIds.length === 0 && (
          <div className="border border-amber-300 bg-amber-50 rounded-sm p-6 text-center text-amber-900 text-[13px]">
            You have not been allocated to any account.<br />
            Ask an administrator to add your account IDs on the Users page.
          </div>
        )}

        {showFilterBar && (
          <div className="mb-3">
            <FieldFilterBar
              moduleKey="bank"
              fields={BANK_FILTER_FIELDS}
              dropdowns={filterDropdowns}
              value={advFilters}
              onChange={setAdvFilters}
            />
          </div>
        )}

        <div className="border border-slate-200 bg-white rounded-sm">
          <table className="erp-table" data-testid="bank-grid">
            <thead>
              <tr>
                {visibleCols.map((c) => <th key={c.k}>{c.label}</th>)}
                {editable && <th></th>}
              </tr>
            </thead>
            <tbody>
              {pag.pageRows.map((r) => (
                <tr key={r.id} data-testid={`bank-row-${r.id}`}>
                  {visibleCols.map((c) => <React.Fragment key={c.k}>{c.render(r)}</React.Fragment>)}
                  {editable && (
                    <td className="whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        {isAdmin && (r.status || "verified") === "pending" && (
                          <>
                            <button onClick={() => verify(r.id)} title="Verify — counts in balance"
                                    className="inline-flex items-center gap-1 h-6 px-1.5 text-[10.5px] font-semibold rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                    data-testid={`bank-verify-${r.id}`}>
                              <Check size={11} /> Verify
                            </button>
                            <button onClick={() => reject(r.id)} title="Reject"
                                    className="inline-flex items-center gap-1 h-6 px-1.5 text-[10.5px] font-semibold rounded-sm border border-red-300 bg-red-50 text-red-800 hover:bg-red-100"
                                    data-testid={`bank-reject-${r.id}`}>
                              <X size={11} /> Reject
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => {
                            setEditingId(r.id);
                            setCreating({
                              payment_transfer_date: r.payment_transfer_date || "",
                              payment_mode: r.payment_mode || "",
                              account_id: r.account_id || "",
                              money_added: r.money_added ?? "",
                              notes: r.notes || "",
                            });
                            setPendingScreenshot(null);
                          }}
                          title="Edit"
                          className="inline-flex items-center justify-center w-6 h-6 rounded-sm text-slate-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`bank-edit-${r.id}`}
                        >
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => del(r.id)} disabled={!deletable} title={deletable ? "Delete" : "Delete permission required"} className={`inline-flex items-center justify-center w-6 h-6 rounded-sm ${deletable ? "text-slate-600 hover:text-red-700 hover:bg-red-50" : "text-slate-300 cursor-not-allowed"}`} data-testid={`bank-delete-${r.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={visibleCols.length + (editable ? 1 : 0) || 1} className="text-center py-6 text-slate-500">
                {visibleCols.length === 0 ? "No columns visible for your team." : "No transfers."}
              </td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-2 flex justify-end">
          <PaginationBar page={pag.page} pageSize={pag.pageSize} total={pag.total}
            onPageChange={pag.setPage} onPageSizeChange={pag.setPageSize} testIdPrefix="bank" />
        </div>
      </PageBody>

      {creating && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={() => { setCreating(null); setEditingId(null); setPendingScreenshot(null); }}>
          <div className="w-[420px] h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4">
              <div className="font-heading text-[14px] font-semibold">{editingId ? "Edit Transfer" : "New Transfer"}</div>
              <button onClick={() => { setCreating(null); setEditingId(null); setPendingScreenshot(null); }} className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-auto p-4 grid grid-cols-2 gap-3">
              {!isAdmin && (
                <div className="col-span-2 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900" data-testid="bank-new-pending-hint">
                  Your entry will be saved as <span className="font-semibold">PENDING</span> until an admin verifies it. Only verified entries count in your balance.
                </div>
              )}
              <Field label="Date"><Input testId={T.formInput("payment_transfer_date")} type="date" value={creating.payment_transfer_date || ""} onChange={(e) => setCreating((s) => ({ ...s, payment_transfer_date: e.target.value }))} /></Field>
              <Field label="Payment Mode"><Select testId={T.formInput("payment_mode")} options={dropdowns.payment_mode || []} value={creating.payment_mode} onChange={(v) => setCreating((s) => ({ ...s, payment_mode: v }))} /></Field>
              <Field label="Account ID" span={2}>
                <Select testId={T.formInput("account_id")} options={allowedAccounts.map((a) => a.account_id)} value={creating.account_id} onChange={(v) => setCreating((s) => ({ ...s, account_id: v }))} />
              </Field>
              <Field label="Amount ₹" span={2}><Input testId={T.formInput("money_added")} type="number" value={creating.money_added ?? ""} onChange={(e) => setCreating((s) => ({ ...s, money_added: Number(e.target.value) }))} /></Field>
              <Field label="Notes" span={2}><Input testId={T.formInput("notes")} value={creating.notes || ""} onChange={(e) => setCreating((s) => ({ ...s, notes: e.target.value }))} /></Field>
              <Field label="Payment Screenshot (proof of transfer)" span={2}>
                <label className="w-full inline-flex items-center gap-2 h-8 px-2 border border-slate-300 rounded-sm bg-white cursor-pointer hover:bg-slate-50 text-[12px]" data-testid="bank-new-screenshot-input">
                  <Upload size={12} />
                  <span className="truncate">{pendingScreenshot ? pendingScreenshot.name : "Click to upload image"}</span>
                  <input type="file" accept="image/*" className="hidden"
                         onChange={(e) => setPendingScreenshot(e.target.files?.[0] || null)} />
                </label>
                {pendingScreenshot && (
                  <div className="mt-2 text-[10.5px] text-slate-500">
                    <img src={URL.createObjectURL(pendingScreenshot)} alt="preview"
                         className="h-24 w-auto object-contain border border-slate-200 rounded-sm bg-slate-50 mt-1" />
                  </div>
                )}
              </Field>
            </div>
            <div className="h-14 border-t border-slate-200 flex items-center justify-end gap-2 px-4">
              <Button onClick={() => { setCreating(null); setEditingId(null); setPendingScreenshot(null); }} testId={T.cancelBtn}>Cancel</Button>
              <Button variant="primary" onClick={save} testId={T.saveBtn}>Save</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
